import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  DashboardLayout,
  PrimaryButton,
  SecondaryButton,
  useDashboard,
} from 'components';
import CardSummary from 'components/CardSummary';
import CaseDistributionChart from './components/CaseDistributionChart';
import EventsTrendChart from './components/EventsTrendChart';
import {calculateTotals} from './helpers';
import {Period} from '../FraudDetection/Reconciliation/components/Period';
import FilterV2 from '../../components/FilterV2';
import {useGatedQuery} from '../FraudDetection/Flows/hooks/useGatedQuery';
import {
  fetchOverview,
  fetchResolvedCasesOverview,
} from '../../requests/queries/flows';

const SetupTriggerButton = () => {
  const {openWidget} = useDashboard();
  const {userDetails} = useSelector(state => state?.auth);

  const setupGuide = userDetails?.company?.setup_guide;

  const step1 = setupGuide?.step1 ? 1 : 0;
  const step2 = setupGuide?.step2 ? 1 : 0;
  const step3 = setupGuide?.step3 ? 1 : 0;
  const completedCount = step1 + step2 + step3;

  const isFullyComplete = completedCount === 3;

  if (isFullyComplete) {
    return (
      <PrimaryButton
        buttonText="Contact Support"
        onClick={() => {
          window.location.href = 'mailto:sales@profiledrisk.com';
        }}
      />
    );
  }

  return (
    <PrimaryButton
      buttonText={`Setup ${completedCount}/3`}
      onClick={openWidget}
    />
  );
};

const ConnectedSetupTriggerButton = SetupTriggerButton;

function ProfiledRiskOverview() {
  const [date, setDate] = useState('day');
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  const handleFilterChange = values => {
    const {dateRange, ...rest} = values;
    delete rest.date;
    setFilters({date_from: dateRange?.start, date_to: dateRange?.end});
  };

  const {data: overviewData, isLoading: overviewLoading} = useGatedQuery(
    ['overview-d', {filters}],
    () => fetchOverview({filters}),
  );
  const {data: resolvedCasesOverview, isLoading: resolvedCasesLoading} =
    useGatedQuery(['resolved-overview', {filters}], () =>
      fetchResolvedCasesOverview({filters}),
    );

  const resolvedCasesData = useMemo(
    () =>
      (!resolvedCasesLoading &&
        resolvedCasesOverview &&
        resolvedCasesOverview?.entity) ||
      [],
    [resolvedCasesOverview, resolvedCasesLoading],
  );

  const totalCases = useMemo(() => {
    if (!resolvedCasesData) return 0;
    return resolvedCasesData.reduce(
      (sum, item) => sum + parseInt(item.count || 0),
      0,
    );
  }, [resolvedCasesData]);

  const resolvedCases = useMemo(() => {
    if (!resolvedCasesData) return 0;
    const filtered = resolvedCasesData?.filter(item =>
      ['ALLOW', 'BLOCK']?.includes(item?.final_outcome),
    );
    return filtered.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
  }, [resolvedCasesData]);

  const unresolvedCases = useMemo(() => {
    if (!overviewData) return 0;
    const filtered = resolvedCasesData?.filter(item =>
      ['PENDING']?.includes(item?.final_outcome),
    );
    return filtered.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
  }, [overviewData, resolvedCasesData]);

  const ALL_COUNT = useMemo(
    () => !overviewLoading && calculateTotals(overviewData?.entity, 'count'),
    [overviewData?.entity, overviewLoading],
  );

  const isProfiledRisk = localStorage.subdomain?.includes('profiledrisk');

  const ALL_ALLOWED = useMemo(
    () =>
      !overviewLoading &&
      calculateTotals(
        overviewData?.entity?.filter(data => data?.final_outcome === 'ALLOW'),
        'count',
      ),
    [overviewData?.entity, overviewLoading],
  );

  const passRatePercentage = useMemo(
    () =>
      (!overviewLoading &&
        ALL_COUNT?.count !== 0 &&
        (ALL_ALLOWED?.count / ALL_COUNT?.count) * 100) ||
      0,
    [ALL_ALLOWED?.count, ALL_COUNT?.count, overviewLoading],
  );

  const resolvedPercentage = useMemo(() => {
    if (totalCases === 0) return 0;
    return Math.round((resolvedCases / totalCases) * 100);
  }, [resolvedCases, totalCases]);

  const unresolvedPercentage = useMemo(() => {
    if (totalCases === 0) return 0;
    return Math.round((unresolvedCases / totalCases) * 100);
  }, [unresolvedCases, totalCases]);

  const caseDistributionData = [
    {label: 'Resolved', percentage: resolvedPercentage, color: '#2E7D32'},
    {
      label: 'Pending review',
      percentage: unresolvedPercentage,
      color: '#F9A825',
    },
  ];

  return (
    <DashboardLayout
      bg="bg-white"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Overview</p>
      }
    >
      <div className="flex items-center py-3 flex-wrap gap-3 justify-end mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />

        {isProfiledRisk && <ConnectedSetupTriggerButton />}
      </div>
      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 filters={[]} onFilterChange={handleFilterChange} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-center gap-2 pt-8 pb-5">
        <CardSummary
          title="High Risk profile"
          body={overviewData?.high_profiles_count || '0'}
          loading={overviewLoading}
        />
        <CardSummary
          title="Open alerts"
          body={overviewData?.open_alerts || '0'}
          loading={overviewLoading}
        />
        <CardSummary
          title="Cases Open"
          body={overviewData?.cases_open_for_over_24_hours || '0'}
          loading={overviewLoading}
        />
        <CardSummary
          title="Pass Rate"
          body={`${(passRatePercentage || 0)?.toFixed(1)}%`}
          loading={overviewLoading}
          count={ALL_COUNT?.count || 0}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-5">
        <div className="border border-[#EFEFEF] rounded-xl">
          <div className="bg-[#F9F9F9] py-3 px-4 rounded-t-xl">
            <h4 className="text-deepBlue font-medium">Alerts trend</h4>
          </div>
          <div className="p-4">
            <EventsTrendChart
              customElements={
                <Period date={date} setDate={setDate} className="!py-2" />
              }
              periodType={date}
              filters={filters}
            />
          </div>
        </div>
        <div className="border border-[#EFEFEF] rounded-xl">
          <div className="bg-[#F9F9F9] py-3 px-4 rounded-t-xl">
            <h4 className="text-deepBlue font-medium">
              Case Distribution by Status
            </h4>
          </div>
          <div className="flex flex-col md:flex-row justify-between md:h-[89%]">
            <div className="md:w-[50%] flex items-center justify-center">
              <CaseDistributionChart
                data={{totalCases, resolvedCases, unresolvedCases}}
              />
            </div>
            <div className="md:w-[50%] px-6 py-5 md:py-0 border border-transparent border-l-[#F2F2F2] flex items-center justify-center flex-col gap-4  ">
              {caseDistributionData.map(({label, percentage, color}) => (
                <div
                  key={label}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-1.5">
                    <div
                      style={{backgroundColor: color}}
                      className="w-2 h-2 rounded-full"
                    />
                    <p className="text-deepBlue text-sm font-normal capitalize">
                      {label}
                    </p>
                  </div>
                  <p className="text-sm text-[#858DAA] font-medium">
                    {percentage}%
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ProfiledRiskOverview;
